import { Anchor, Button, Group, SegmentedControl, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { DataTableColumn } from 'mantine-datatable';
import { Link } from 'react-router';
import { Table } from 'src/common/components/table/Table';
import type { MatchSuggestion } from '../../types';
import FeedReferenceWithComparison from './FeedReferenceWithComparison';
import MatchReasonPopover from './MatchReasonPopover';
import { RelativeTime, ResolvedCell, StatusBadge } from './SuggestionMeta';

type SuggestionReviewTableProps = {
  suggestions: MatchSuggestion[];
  headerAction: React.ReactNode;
  showStatusFilter?: boolean;
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
  onUnlink: (id: string) => void;
  onReopen: (id: string) => void;
};

const columns: DataTableColumn<MatchSuggestion>[] = [
  {
    accessor: 'unprocessedProduct.providerReferenceNumber',
    title: 'Feed Reference',
    textAlign: 'right',
    render: suggestion => <FeedReferenceWithComparison suggestion={suggestion} />,
  },
  {
    accessor: 'suggestedAsset.providerReferenceNumber',
    title: 'Asset Reference',
    render: ({ suggestedAsset }) => (
      <Anchor
        component={Link}
        to={`/client/${suggestedAsset.clientId}/financial-items/${suggestedAsset.product}/${suggestedAsset.id}`}
        size="sm"
      >
        {suggestedAsset.providerReferenceNumber}
      </Anchor>
    ),
  },
  {
    accessor: 'suggestedAsset.clientName',
    title: 'Client',
    render: ({ suggestedAsset }) => (
      <Anchor component={Link} to={`/client/${suggestedAsset.clientId}`} size="sm" fw={500}>
        {suggestedAsset.clientName}
      </Anchor>
    ),
  },
  {
    accessor: 'matchReasons',
    title: 'Why Matched',
    render: suggestion => <MatchReasonPopover suggestion={suggestion} />,
  },
  {
    accessor: 'status',
    title: 'Status',
    render: ({ status }) => <StatusBadge status={status} />,
  },
  {
    accessor: 'createdAt',
    title: 'Suggested',
    render: ({ createdAt }) => <RelativeTime datetime={createdAt} />,
  },
  {
    accessor: 'resolvedAt',
    title: 'Resolved',
    render: ({ resolvedBy, resolvedAt }) => (
      <ResolvedCell resolvedBy={resolvedBy} resolvedAt={resolvedAt} />
    ),
  },
];

export default function SuggestionReviewTable({
  suggestions,
  headerAction,
  showStatusFilter = true,
  onConfirm,
  onReject,
  onUnlink,
  onReopen,
}: SuggestionReviewTableProps) {
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const filtered =
    !showStatusFilter || statusFilter === 'ALL'
      ? suggestions
      : suggestions.filter(s => s.status === statusFilter);

  const actionColumn: DataTableColumn<MatchSuggestion> = {
    accessor: 'actions',
    title: '',
    textAlign: 'right',
    render: suggestion => {
      switch (suggestion.status) {
        case 'PENDING':
          return (
            <Group gap="xs" justify="flex-end">
              <Button
                variant="subtle"
                size="compact-xs"
                color="green"
                onClick={() => onConfirm(suggestion.id)}
              >
                Link
              </Button>
              <Button
                variant="subtle"
                size="compact-xs"
                color="red"
                onClick={() => onReject(suggestion.id)}
              >
                Reject
              </Button>
            </Group>
          );
        case 'CONFIRMED':
          return (
            <Button
              variant="subtle"
              size="compact-xs"
              color="gray"
              onClick={() => onUnlink(suggestion.id)}
            >
              Unlink
            </Button>
          );
        case 'REJECTED':
        case 'UNLINKED':
          // TODO: backend needs a reopen endpoint (REJECTED/UNLINKED → PENDING)
          // TODO: backend should guard against reopening if the asset is already feed-connected
          return (
            <Button
              variant="subtle"
              size="compact-xs"
              color="gray"
              onClick={() => onReopen(suggestion.id)}
            >
              Reopen
            </Button>
          );
        default:
          return null;
      }
    },
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="md">
          {showStatusFilter && (
            <SegmentedControl
              size="xs"
              value={statusFilter}
              onChange={setStatusFilter}
              data={[
                { value: 'PENDING', label: 'Pending' },
                { value: 'CONFIRMED', label: 'Confirmed' },
                { value: 'REJECTED', label: 'Rejected' },
                { value: 'UNLINKED', label: 'Unlinked' },
                { value: 'ALL', label: 'All' },
              ]}
            />
          )}
          <Text size="xs" c="dimmed">
            Click a feed reference to compare full details.
          </Text>
        </Group>
        {headerAction}
      </Group>
      {filtered.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No {statusFilter === 'ALL' ? '' : statusFilter.toLowerCase()} matches for this provider.
        </Text>
      ) : (
        <Table<MatchSuggestion>
          columns={[...columns, actionColumn]}
          data={filtered}
          isLoading={false}
          onRowClick={() => {}}
        />
      )}
    </Stack>
  );
}
